import { Link } from 'react-router-dom';
import { useAffiliatePageOffer } from './AffiliatePageProvider';
import type { AffiliatePlacementSlot } from '../lib/affiliateContext';

type AffiliateOfferCardProps = {
  offer: {
    id: string;
    title: string;
    blurb: string;
    url: string;
    disclosure: string;
  };
  disclosure: string | null;
  slot: AffiliatePlacementSlot;
};

function AffiliateOfferCard({ offer, disclosure, slot }: AffiliateOfferCardProps) {
  const inline = slot === 'inline';

  return (
    <aside
      className={
        inline
          ? 'rounded-2xl border border-sky-500/25 bg-gradient-to-br from-sky-500/[0.08] to-cyan-500/[0.04] px-4 py-3.5 shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]'
          : 'rounded-2xl border border-sky-500/20 bg-gradient-to-br from-sky-500/[0.06] to-white/[0.02] px-4 py-3.5'
      }
      aria-label="Partner recommendation"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-sky-300/90">
            {inline ? 'Suggested gear' : 'Partner pick'}
          </p>
          <p className="mt-1 text-sm font-semibold text-zinc-100">{offer.title}</p>
          <p className="mt-1 text-xs leading-relaxed text-zinc-400">{offer.blurb}</p>
        </div>
        <a
          href={offer.url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          className={
            inline
              ? 'shrink-0 rounded-xl bg-sky-500/90 px-4 py-2.5 text-center text-xs font-semibold text-white shadow-sm transition hover:bg-sky-400'
              : 'shrink-0 rounded-xl border border-sky-400/30 bg-sky-500/15 px-4 py-2.5 text-center text-xs font-semibold text-sky-100 transition hover:bg-sky-500/25'
          }
        >
          View on Amazon
        </a>
      </div>
      <p className="mt-2.5 text-[10px] leading-snug text-zinc-500">
        {disclosure ?? offer.disclosure}{' '}
        <Link to="/profile#ad-free" className="text-zinc-400 underline-offset-2 hover:text-zinc-300 hover:underline">
          Go ad-free
        </Link>
      </p>
    </aside>
  );
}

type AffiliatePlacementProps = {
  slot?: AffiliatePlacementSlot;
  className?: string;
};

export function AffiliatePlacement({ slot = 'inline', className }: AffiliatePlacementProps) {
  const { offer, disclosure } = useAffiliatePageOffer(slot);
  if (!offer) return null;
  return (
    <div className={className}>
      <AffiliateOfferCard offer={offer} disclosure={disclosure} slot={slot} />
    </div>
  );
}
